/**
 * Teslyn licence tokens (ADR-005; docs/specs/cloud/licences.md §3, §5): the claims, the limits MB decided
 * (decision 106: valid 30 days, then 60 days of grace), the PASETO v4.public encoding the Edge Function signs with,
 * and the time windows Teslyn Field shows. Signatures are verified in Rust (`crates/teslyn-core/src/licence.rs`);
 * this module never holds a key: `encodeLicenceToken` signs through a function the caller gives it.
 *
 * No imports on purpose: the Edge Functions (Deno) use an identical copy, `supabase/functions/_shared/licence.ts`,
 * and `test/licence.test.ts` fails if the two differ (`node tools/dev/copy-edge-shared.mjs`).
 */

/** Version of the claims below; Field refuses a token whose version it does not know. */
export const LICENCE_CLAIMS_VERSION = 1;
/** A licence token is valid for 30 days from issue and renewed at every check-in (ADR-005, decision 106). */
export const LICENCE_VALID_DAYS = 30;
/** Then 60 days of grace (decision 106), so a laptop can be off-grid up to 90 days. */
export const LICENCE_GRACE_DAYS = 60;
/** Signed into every licence token but never sent: a token signed for another purpose is never a Field licence. */
export const LICENCE_IMPLICIT_ASSERTION = 'survera-field-licence:v1';
/** A laptop clock up to this far behind the issue time is not flagged. */
export const LICENCE_CLOCK_SKEW_MINUTES = 5;
/** The hard-stop banner shows from 30 days before it (decision 294), that is after 60 days without a check-in. */
export const LICENCE_HARD_STOP_NOTICE_DAYS = 30;
/** ...and turns red in the last 7 days. */
export const LICENCE_HARD_STOP_URGENT_DAYS = 7;
/** The subscription banner shows from 30 days before the end date Braynewave recorded (decision 294). */
export const LICENCE_SUBSCRIPTION_NOTICE_DAYS = 30;
/** A device checks in when its token is this old (once a day, not every 2 minutes as the Diurnal tool). */
export const LICENCE_CHECKIN_EVERY_HOURS = 24;

/** The devices a seat covers (decision 289): 1 laptop (Teslyn Field) and 2 mobiles, each kind counted apart. */
export const LICENCE_DEVICE_KINDS = ['laptop', 'mobile'] as const;
export type LicenceDeviceKind = (typeof LICENCE_DEVICE_KINDS)[number];

const KID = /^[a-z0-9-]{1,40}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const FINGERPRINT = /^[0-9a-f]{64}$/;
const PLAN = /^[a-z0-9_]{1,40}$/;
const FEATURE = /^[a-z0-9_.-]{1,40}$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;
const HEADER = 'v4.public.';

const isDeviceKind = (k: unknown): k is LicenceDeviceKind =>
  typeof k === 'string' && (LICENCE_DEVICE_KINDS as readonly string[]).includes(k);

/**
 * The platform a device reports at check-in (licence_activation.device_platform, 20261028150000): a laptop runs
 * Teslyn Field on Windows, macOS or Linux; a mobile is the phone app (batch 7) on iOS or Android.
 */
export const LICENCE_DEVICE_PLATFORMS = {
  laptop: ['windows', 'macos', 'linux'],
  mobile: ['ios', 'android'],
} as const satisfies Record<LicenceDeviceKind, readonly string[]>;
export type LicenceDevicePlatform = (typeof LICENCE_DEVICE_PLATFORMS)[LicenceDeviceKind][number];

/** Whether `platform` is one a device of `kind` can run on (the database refuses any other). */
export function platformFitsKind(
  kind: LicenceDeviceKind,
  platform: unknown,
): platform is LicenceDevicePlatform {
  return (
    typeof platform === 'string' && (LICENCE_DEVICE_PLATFORMS[kind] as readonly string[]).includes(platform)
  );
}

/** A real calendar date written `YYYY-MM-DD`. */
function isCalendarDate(s: string): boolean {
  const m = DATE.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10) === s;
}

/** The claims of a licence token, by their ADR-005 names (spec §3.2). Timestamps are `YYYY-MM-DDTHH:MM:SSZ`. */
export interface LicenceClaims {
  readonly v: number;
  readonly token_id: string;
  readonly tenant_id: string;
  readonly user_id: string;
  readonly device_kind: LicenceDeviceKind;
  readonly machine_fingerprint: string;
  readonly plan: string;
  readonly features: readonly string[];
  readonly seats_snapshot: number;
  /** The subscription end date Braynewave recorded, `YYYY-MM-DD`, or null (decision 294). */
  readonly subscription_ends: string | null;
  readonly iat: string;
  readonly nbf: string;
  readonly not_after: string;
  readonly grace_days: number;
}

/** What the Edge Function knows when it issues a token. */
export interface LicenceIssue {
  readonly tokenId: string;
  readonly tenantId: string;
  readonly userId: string;
  readonly deviceKind: LicenceDeviceKind;
  readonly machineFingerprint: string;
  readonly plan: string;
  readonly features: readonly string[];
  readonly seats: number;
  readonly subscriptionEnds: string | null;
  readonly issuedAt: Date;
}

/** An instant as licence tokens write it: UTC, whole seconds, `Z`. */
export function licenceTime(at: Date | number): string {
  const ms = typeof at === 'number' ? at : at.getTime();
  if (!Number.isFinite(ms)) throw new Error('Licence time is not a valid time.');
  return new Date(Math.floor(ms / 1000) * 1000).toISOString().replace('.000Z', 'Z');
}

/** `not_after` of a token issued at `issuedAt`: 30 days on, whole seconds (the database records it first). */
export function licenceNotAfter(issuedAt: Date): string {
  const t = issuedAt.getTime();
  if (!Number.isFinite(t)) throw new Error('Licence time is not a valid time.');
  return licenceTime(Math.floor(t / 1000) * 1000 + LICENCE_VALID_DAYS * DAY_MS);
}

/** The claims of a new token: valid 30 days from `issuedAt`, 60 days of grace. Throws on a malformed issue. */
export function buildLicenceClaims(issue: LicenceIssue): LicenceClaims {
  const fail = (what: string): never => {
    throw new Error(`Cannot issue a licence: ${what}.`);
  };
  if (!UUID.test(issue.tokenId)) fail('the token id is not a uuid');
  if (!UUID.test(issue.tenantId)) fail('the tenant id is not a uuid');
  if (!UUID.test(issue.userId)) fail('the user id is not a uuid');
  if (!isDeviceKind(issue.deviceKind)) fail('the device kind must be laptop or mobile');
  if (!FINGERPRINT.test(issue.machineFingerprint))
    fail('the device fingerprint is not 64 lower-case hex digits');
  if (issue.subscriptionEnds !== null && !isCalendarDate(issue.subscriptionEnds))
    fail('the subscription end must be a date written YYYY-MM-DD');
  if (!PLAN.test(issue.plan)) fail('the plan name must be lower-case letters, digits or _');
  if (!Number.isInteger(issue.seats) || issue.seats < 0 || issue.seats > 10_000)
    fail('seats must be 0 to 10000');
  for (const f of issue.features) if (!FEATURE.test(f)) fail(`the feature name "${f}" is not allowed`);
  const t = issue.issuedAt.getTime();
  if (!Number.isFinite(t)) fail('the issue time is not a valid time');
  const iat = Math.floor(t / 1000) * 1000;
  return {
    v: LICENCE_CLAIMS_VERSION,
    token_id: issue.tokenId,
    tenant_id: issue.tenantId,
    user_id: issue.userId,
    device_kind: issue.deviceKind,
    machine_fingerprint: issue.machineFingerprint,
    plan: issue.plan,
    features: [...issue.features],
    seats_snapshot: issue.seats,
    subscription_ends: issue.subscriptionEnds,
    iat: licenceTime(iat),
    nbf: licenceTime(iat),
    not_after: licenceNotAfter(issue.issuedAt),
    grace_days: LICENCE_GRACE_DAYS,
  };
}

// ---------------------------------------------------------------- PASETO v4.public encoding

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

/** PASETO's pre-authentication encoding: the piece count, then each piece's length and bytes (64-bit LE). */
export function paeV4(pieces: readonly Uint8Array[]): Uint8Array {
  const le64 = (n: number): number[] => {
    const out: number[] = [];
    let v = n;
    for (let i = 0; i < 8; i++) {
      out.push(i === 7 ? v & 0x7f : v & 0xff);
      v = Math.floor(v / 256);
    }
    return out;
  };
  const total = 8 + pieces.reduce((s, p) => s + 8 + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  const put = (bytes: ArrayLike<number>): void => {
    out.set(bytes, at);
    at += bytes.length;
  };
  put(le64(pieces.length));
  for (const p of pieces) {
    put(le64(p.length));
    put(p);
  }
  return out;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Base64url without padding (RFC 4648 §5), as PASETO uses. */
export function base64urlEncode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    const chars = i + 2 < bytes.length ? 4 : i + 1 < bytes.length ? 3 : 2;
    for (let c = 0; c < chars; c++) s += ALPHABET[(n >> (18 - 6 * c)) & 63];
  }
  return s;
}

/** Strict base64url: no padding, URL alphabet only, no impossible length, spare bits zero. Throws otherwise. */
export function base64urlDecode(s: string): Uint8Array {
  if (s.length % 4 === 1) throw new Error('Invalid base64url length.');
  const out: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of s) {
    const v = ALPHABET.indexOf(ch);
    if (v < 0) throw new Error('Invalid base64url character.');
    value = (value << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((value >> bits) & 0xff);
      value &= (1 << bits) - 1;
    }
  }
  if (value !== 0) throw new Error('Invalid base64url: non-zero spare bits.');
  return Uint8Array.from(out);
}

/** The message a v4.public signature covers. */
export function v4PublicMessage(payload: Uint8Array, footer: Uint8Array, implicit: Uint8Array): Uint8Array {
  return paeV4([utf8(HEADER), payload, footer, implicit]);
}

/** The parts of a v4.public token, NOT verified (verification is in Rust, or WebCrypto in tests). */
export function v4PublicParts(token: string): {
  readonly payload: Uint8Array;
  readonly signature: Uint8Array;
  readonly footer: Uint8Array;
} {
  const parts = token.split('.');
  if (parts.length < 3 || parts.length > 4 || parts[0] !== 'v4' || parts[1] !== 'public')
    throw new Error('Not a v4.public token.');
  if (parts.length === 4 && parts[3] === '') throw new Error('An empty footer must be left out.');
  const body = base64urlDecode(parts[2]!);
  if (body.length < 64) throw new Error('The token is too short to hold a signature.');
  return {
    payload: body.slice(0, body.length - 64),
    signature: body.slice(body.length - 64),
    footer: parts.length === 4 ? base64urlDecode(parts[3]!) : new Uint8Array(),
  };
}

/**
 * A signed licence token: `v4.public.<payload ‖ signature>.<{"kid":…}>`, signed over PAE with the implicit
 * assertion. `sign` returns the 64-byte Ed25519 signature of the message it is given (the Edge Function's key).
 */
export async function encodeLicenceToken(
  claims: LicenceClaims,
  kid: string,
  sign: (message: Uint8Array) => Promise<Uint8Array> | Uint8Array,
): Promise<string> {
  if (!KID.test(kid)) throw new Error('The key id must be 1 to 40 lower-case letters, digits or hyphens.');
  const payload = utf8(JSON.stringify(claims));
  const footer = utf8(JSON.stringify({ kid }));
  const signature = await sign(v4PublicMessage(payload, footer, utf8(LICENCE_IMPLICIT_ASSERTION)));
  if (signature.length !== 64) throw new Error('An Ed25519 signature must be 64 bytes.');
  const body = new Uint8Array(payload.length + 64);
  body.set(payload);
  body.set(signature, payload.length);
  return `${HEADER}${base64urlEncode(body)}.${base64urlEncode(footer)}`;
}

// ---------------------------------------------------------------- time windows (spec §5)

export type LicencePhase = 'valid' | 'grace' | 'ended';

export interface LicenceWindow {
  readonly phase: LicencePhase;
  /** The time the window was judged at: the later of the clock and the latest time seen (clock set back). */
  readonly now: string;
  readonly notAfter: string;
  /** `not_after + grace_days`: the licence has ended from this instant. */
  readonly hardStop: string;
  /** Whole days left, counted down; 0 once passed. */
  readonly daysToNotAfter: number;
  readonly daysToHardStop: number;
  /** The clock is more than 5 minutes behind the issue time (shown, never enforced). */
  readonly clockBehind: boolean;
  /** The clock is behind the latest time this laptop has seen. */
  readonly clockSetBack: boolean;
}

/**
 * Where a licence stands at `now`. `lastSeen` is the latest time Field has seen on this laptop; using the later of
 * the two means winding the clock back cannot extend a licence. A clock behind the issue time is treated as valid,
 * because a wrong clock must never stop a crew (spec §5).
 */
export function licenceWindow(
  claims: Pick<LicenceClaims, 'nbf' | 'not_after' | 'grace_days'>,
  now: Date,
  lastSeen?: Date | null,
): LicenceWindow {
  const clock = now.getTime();
  const seen = lastSeen?.getTime();
  const setBack = seen !== undefined && Number.isFinite(seen) && seen > clock;
  const t = setBack ? (seen as number) : clock;
  const nbf = Date.parse(claims.nbf);
  const notAfter = Date.parse(claims.not_after);
  const hardStop = notAfter + claims.grace_days * DAY_MS;
  const days = (until: number): number => Math.max(0, Math.floor((until - t) / DAY_MS));
  return {
    phase: t < notAfter ? 'valid' : t < hardStop ? 'grace' : 'ended',
    now: licenceTime(t),
    notAfter: licenceTime(notAfter),
    hardStop: licenceTime(hardStop),
    daysToNotAfter: days(notAfter),
    daysToHardStop: days(hardStop),
    clockBehind: t < nbf - LICENCE_CLOCK_SKEW_MINUTES * 60_000,
    clockSetBack: setBack,
  };
}

export type LicenceMatch = 'ok' | 'other_tenant' | 'other_user' | 'other_machine';

/** Whether a token belongs to this tenant, this signed-in person and this laptop. */
export function licenceMatches(
  claims: Pick<LicenceClaims, 'tenant_id' | 'user_id' | 'machine_fingerprint'>,
  who: { readonly tenantId: string; readonly userId: string; readonly machineFingerprint: string },
): LicenceMatch {
  if (claims.tenant_id !== who.tenantId) return 'other_tenant';
  if (claims.user_id !== who.userId) return 'other_user';
  if (claims.machine_fingerprint !== who.machineFingerprint) return 'other_machine';
  return 'ok';
}

// ---------------------------------------------------------------- banners (spec §8, decision 294)

export type LicenceBannerLevel = 'warn' | 'fail';

/**
 * A licence banner for Teslyn Field. `hard_stop`: `date` is the hard stop (RFC 3339), `days` the whole days left.
 * `subscription`: `date` is the subscription end date (`YYYY-MM-DD`, the last day covered), `days` the whole days
 * left, 0 once passed. The words are Field's; the thresholds are here.
 */
export interface LicenceBanner {
  readonly kind: 'hard_stop' | 'subscription';
  readonly level: LicenceBannerLevel;
  readonly date: string;
  readonly days: number;
}

/**
 * The banners to show at `now` (decision 294): the countdown to the hard stop from 30 days before it (amber; red in
 * the last 7 days), and the subscription end from 30 days before it (amber; red once passed). None once the licence
 * has ended: Field then shows that it is read only. `lastSeen` as in `licenceWindow`.
 */
export function licenceBanners(
  claims: Pick<LicenceClaims, 'nbf' | 'not_after' | 'grace_days' | 'subscription_ends'>,
  now: Date,
  lastSeen?: Date | null,
): LicenceBanner[] {
  const w = licenceWindow(claims, now, lastSeen);
  const t = Date.parse(w.now);
  const out: LicenceBanner[] = [];
  if (w.phase === 'ended') return out;
  const toStop = Date.parse(w.hardStop) - t;
  if (toStop <= LICENCE_HARD_STOP_NOTICE_DAYS * DAY_MS)
    out.push({
      kind: 'hard_stop',
      level: toStop <= LICENCE_HARD_STOP_URGENT_DAYS * DAY_MS ? 'fail' : 'warn',
      date: w.hardStop,
      days: w.daysToHardStop,
    });
  const sub = subscriptionBanner(claims.subscription_ends, new Date(t));
  if (sub) out.push(sub);
  return out;
}

/**
 * The subscription banner at `now` (decision 294), for Teslyn Field's banners and Teslyn Cloud's Licences page: from
 * 30 days before the end date Braynewave recorded (amber), red once it has passed; null before that or without a
 * date. The date is the last day covered: the subscription ends at the end of that day (UTC).
 */
export function subscriptionBanner(ends: string | null | undefined, now: Date): LicenceBanner | null {
  if (!ends || !isCalendarDate(ends)) return null;
  const left = Date.parse(`${ends}T00:00:00Z`) + DAY_MS - now.getTime();
  if (left > LICENCE_SUBSCRIPTION_NOTICE_DAYS * DAY_MS) return null;
  return {
    kind: 'subscription',
    level: left <= 0 ? 'fail' : 'warn',
    date: ends,
    days: Math.max(0, Math.floor(left / DAY_MS)),
  };
}

/** Whole days since `at` (a check-in), 0 for a time in the future, null when there is none. */
export function daysSince(at: string | null, now: Date): number | null {
  if (at === null) return null;
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

/**
 * Whether a device should check in now: it has no token, or its token was issued a day ago or more. A token issued
 * a day or more in the future (a clock that is wrong) also asks for a fresh one.
 */
export function checkinDue(stored: Pick<LicenceClaims, 'iat'> | null, now: Date): boolean {
  if (!stored) return true;
  const iat = Date.parse(stored.iat);
  if (!Number.isFinite(iat)) return true;
  return Math.abs(now.getTime() - iat) >= LICENCE_CHECKIN_EVERY_HOURS * 3_600_000;
}

// ---------------------------------------------------------------- check-in (spec §6.1)

/** What a device sends to `licence-checkin`. */
export interface LicenceCheckinRequest {
  readonly tenantId: string;
  readonly deviceKind: LicenceDeviceKind;
  readonly machineFingerprint: string;
  /** The device's name for people (the Windows computer name), at most 80 characters. */
  readonly machineLabel: string;
  readonly appVersion: string;
  /** True after an explicit sign-in, or when the device has no stored licence for the tenant (spec §6.2). */
  readonly activate: boolean;
  /** The platform it runs on, when it says (the phone app will; Teslyn Field up to 2026.10.12 does not). */
  readonly platform: LicenceDevicePlatform | null;
}

const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g; // eslint-disable-line no-control-regex

/**
 * The request body, checked: `{ tenant_id, device_kind, machine_fingerprint, machine_label, app_version, activate,
 * platform? }`.
 */
export function parseCheckinRequest(
  body: unknown,
):
  | { readonly ok: true; readonly value: LicenceCheckinRequest }
  | { readonly ok: false; readonly error: string } {
  const no = (error: string): { readonly ok: false; readonly error: string } => ({ ok: false, error });
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return no('Send a JSON object.');
  const b = body as Record<string, unknown>;
  if (typeof b.tenant_id !== 'string' || !UUID.test(b.tenant_id)) return no('Choose an organisation.');
  if (!isDeviceKind(b.device_kind)) return no('The device kind must be laptop or mobile.');
  if (typeof b.machine_fingerprint !== 'string' || !FINGERPRINT.test(b.machine_fingerprint))
    return no('The device fingerprint is not valid.');
  const version = b.app_version ?? '';
  if (typeof version !== 'string' || version.length > 40) return no('The app version is not valid.');
  if (b.activate !== undefined && typeof b.activate !== 'boolean')
    return no('activate must be true or false.');
  const platform = b.platform ?? null;
  if (platform !== null && !platformFitsKind(b.device_kind, platform))
    return no('The platform does not fit the device kind.');
  const label = typeof b.machine_label === 'string' ? b.machine_label : '';
  return {
    ok: true,
    value: {
      tenantId: b.tenant_id,
      deviceKind: b.device_kind,
      machineFingerprint: b.machine_fingerprint,
      machineLabel: label.replace(CONTROL_CHARS, '').trim().slice(0, 80),
      appVersion: version,
      activate: b.activate === true,
      platform,
    },
  };
}

/** A seat holder in a "no free seat" answer (decision 296). */
export interface LicenceSeatHolder {
  readonly user_id: string;
  readonly name: string;
  readonly email: string;
  readonly last_checkin_at: string | null;
  readonly days_since_checkin: number | null;
}

/** A device of the person's seat in a "device limit" answer. */
export interface LicenceDeviceHolder {
  readonly activation_id: string;
  readonly machine_label: string;
  readonly last_checkin_at: string | null;
  readonly days_since_checkin: number | null;
}

/**
 * Answers that end a device's licence at its next start (decisions 290, 293, 297). `phone_only`: a pilot-only sign-in
 * asked for a laptop (decisions 917, 918: pilots use Teslyn Mobile only).
 */
export const LICENCE_ENDED_STATUSES = [
  'released',
  'revoked',
  'removed',
  'not_member',
  'no_licence',
  'tenant_cancelled',
  'phone_only',
] as const;
export type LicenceEndedStatus = (typeof LICENCE_ENDED_STATUSES)[number];

/** What `licence-checkin` answers (spec §6.1). */
export type LicenceCheckinAnswer =
  | {
      readonly status: 'active';
      readonly token: string;
      readonly activation_id: string;
      readonly not_after: string;
      readonly hard_stop: string;
      readonly subscription_ends: string | null;
      /** A pilot-only sign-in's phone: on no seat (decision 918). */
      readonly pilot?: true;
    }
  | { readonly status: LicenceEndedStatus }
  | {
      readonly status: 'no_seat';
      readonly seats: number;
      readonly in_use: number;
      readonly holders: readonly LicenceSeatHolder[];
    }
  | {
      readonly status: 'device_limit';
      readonly device_kind: LicenceDeviceKind;
      readonly limit: number;
      readonly holders: readonly LicenceDeviceHolder[];
      /** The limit is a pilot-only sign-in's (decision 918). */
      readonly pilot?: true;
    };

const isStr = (v: unknown): v is string => typeof v === 'string';
const isStrOrNull = (v: unknown): v is string | null => v === null || typeof v === 'string';
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isCountOrNull = (v: unknown): v is number | null => v === null || isCount(v);
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** An answer from `licence-checkin`, checked; null for anything else (an error, a proxy page). */
export function parseCheckinAnswer(body: unknown): LicenceCheckinAnswer | null {
  if (!isRecord(body)) return null;
  const s = body.status;
  if (s === 'active') {
    const ends = body.subscription_ends ?? null;
    if (!isStr(body.token) || !isStr(body.activation_id) || !isStr(body.not_after) || !isStr(body.hard_stop))
      return null;
    if (!isStrOrNull(ends)) return null;
    return {
      status: 'active',
      token: body.token,
      activation_id: body.activation_id,
      not_after: body.not_after,
      hard_stop: body.hard_stop,
      subscription_ends: ends,
      ...(body.pilot === true ? { pilot: true as const } : {}),
    };
  }
  const ended = LICENCE_ENDED_STATUSES.find((x) => x === s);
  if (ended) return { status: ended };
  if (s === 'no_seat') {
    if (!isCount(body.seats) || !isCount(body.in_use) || !Array.isArray(body.holders)) return null;
    const holders: LicenceSeatHolder[] = [];
    for (const h of body.holders as unknown[]) {
      if (!isRecord(h)) return null;
      const { user_id, name, email, last_checkin_at, days_since_checkin } = h;
      if (!isStr(user_id) || !isStr(name) || !isStr(email)) return null;
      if (!isStrOrNull(last_checkin_at) || !isCountOrNull(days_since_checkin)) return null;
      holders.push({ user_id, name, email, last_checkin_at, days_since_checkin });
    }
    return { status: 'no_seat', seats: body.seats, in_use: body.in_use, holders };
  }
  if (s === 'device_limit') {
    if (!isDeviceKind(body.device_kind) || !isCount(body.limit) || !Array.isArray(body.holders)) return null;
    const holders: LicenceDeviceHolder[] = [];
    for (const h of body.holders as unknown[]) {
      if (!isRecord(h)) return null;
      const { activation_id, machine_label, last_checkin_at, days_since_checkin } = h;
      if (!isStr(activation_id) || !isStr(machine_label)) return null;
      if (!isStrOrNull(last_checkin_at) || !isCountOrNull(days_since_checkin)) return null;
      holders.push({ activation_id, machine_label, last_checkin_at, days_since_checkin });
    }
    return {
      status: 'device_limit',
      device_kind: body.device_kind,
      limit: body.limit,
      holders,
      ...(body.pilot === true ? { pilot: true as const } : {}),
    };
  }
  return null;
}
